import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, AreaChart, Area, BarChart, Bar,
} from "recharts";
import {
  Box, Stack, Typography, Paper, Button, ToggleButton, ToggleButtonGroup,
  Chip, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, LinearProgress, Skeleton, Divider,
  IconButton, Menu, MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVertOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUpOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoneyOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartOutlined";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import PercentIcon from "@mui/icons-material/PercentOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import { ChevronUp, ChevronDown, Flame } from "lucide-react";
import { apiGet, apiPost } from "../api/client";
import { useToast } from "../ui/Toaster";
import StatCard from "../components/StatCard";
import Confetti from "../components/Confetti";
import { playChing } from "../components/chingSound";

const SEEN_WINS_KEY = "bidrl_seen_win_ids";

const PIE_COLORS = ["#818cf8", "#34d399", "#f59e0b", "#f87171", "#a5b4fc", "#fbbf24", "#22d3ee", "#fb923c"];
const TIMEFRAMES = [
  { id: "24h", label: "24h" },
  { id: "7d",  label: "7d" },
  { id: "30d", label: "30d" },
  { id: "1y",  label: "1y" },
  { id: "all", label: "All" },
];

const currency = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const compact = (n) => Number(n || 0).toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });
const percent = (n, d = 1) => `${Number(n || 0).toFixed(d)}%`;
const formatShortDate = (s) => {
  if (!s) return "—";
  try {
    let d;
    // Coerce numeric strings ("1778813948.0") AND number values into a
    // proper Date. The wiz/rasmus importers store purchase_date as a
    // stringified UNIX timestamp in seconds, so we have to detect that
    // and multiply by 1000 — without this, ``new Date("1778813948.0")``
    // fails to parse and the raw timestamp falls through to the UI.
    const asNum = typeof s === "number" ? s : Number(s);
    if (Number.isFinite(asNum) && asNum > 0) {
      // Heuristic: anything under 1e12 (~year 2001 in ms) is seconds,
      // above that is already milliseconds. Covers both wiz seconds
      // (1.7e9) and any path that already stored ms.
      const ms = asNum < 1e12 ? asNum * 1000 : asNum;
      d = new Date(ms);
    } else {
      d = new Date(s);
    }
    if (isNaN(d.getTime())) return String(s);
    return d.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" });
  } catch {
    return String(s);
  }
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1.25,
        py: 0.75,
        fontSize: 12,
        bgcolor: "background.paper",
      }}
    >
      {label && (
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>{label}</Typography>
      )}
      {payload.map((p, i) => (
        <Stack key={i} direction="row" alignItems="center" gap={0.75} sx={{ fontSize: 12 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: p.color || p.fill || "#818cf8",
            }}
          />
          <Typography component="span" sx={{ color: "text.secondary", fontSize: 12 }}>
            {p.name}:
          </Typography>
          <Typography component="span" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
            {typeof p.value === "number"
              ? (p.name?.toLowerCase().includes("profit") || p.name?.toLowerCase().includes("revenue")
                  ? currency(p.value)
                  : p.value)
              : p.value}
          </Typography>
        </Stack>
      ))}
    </Paper>
  );
}

export default function Home() {
  const { error: toastError } = useToast();

  const [timeframe, setTimeframe] = useState("30d");
  const [data, setData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [game, setGame] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [confettiKey, setConfettiKey] = useState(0);

  // Stats and invoice data load independently — neither section blocks the
  // other, and the page shell + skeletons paint on first frame so navigation
  // away from this page is never visually blocked by an in-flight fetch.
  const loadStats = useCallback(async (signal) => {
    setStatsLoading(true);
    try {
      const stats = await apiGet("/api/stats", { signal });
      if (signal?.aborted) return;
      setData(stats || {});
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load dashboard stats", e?.message || String(e));
    } finally {
      if (!signal?.aborted) setStatsLoading(false);
    }
  }, [toastError]);

  const loadInvoices = useCallback(async (signal) => {
    setInvoicesLoading(true);
    try {
      const invoices = await apiGet("/api/stats_by_invoice", { signal });
      if (signal?.aborted) return;
      setInvoiceData(Array.isArray(invoices) ? invoices : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load invoice analytics", e?.message || String(e));
    } finally {
      if (!signal?.aborted) setInvoicesLoading(false);
    }
  }, [toastError]);

  const loadGame = useCallback(async (signal) => {
    try {
      const g = await apiGet("/api/gamification", { signal });
      if (signal?.aborted) return;
      setGame(g || {});

      // Confetti + sound for any recent win we haven't celebrated yet.
      const wins = Array.isArray(g?.recent_wins) ? g.recent_wins : [];
      let seen;
      try { seen = new Set(JSON.parse(localStorage.getItem(SEEN_WINS_KEY) || "[]")); }
      catch { seen = new Set(); }
      const fresh = wins.filter((w) => w.id != null && !seen.has(w.id));
      if (fresh.length > 0) {
        setConfettiKey((k) => k + 1);
        try { playChing(); } catch { /* audio is best-effort */ }
      }
      // Remember every recent win so the next visit only celebrates new ones,
      // and trim to the active recent set so the list doesn't grow forever.
      const next = wins.map((w) => w.id).filter((x) => x != null);
      localStorage.setItem(SEEN_WINS_KEY, JSON.stringify(next));
    } catch (e) {
      if (e?.name === "AbortError") return;
      // Silent — gamification is non-critical, no toast spam.
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    loadStats(ctrl.signal);
    loadInvoices(ctrl.signal);
    loadGame(ctrl.signal);
    return () => ctrl.abort();
  }, [loadStats, loadInvoices, loadGame]);

  const refresh = async () => {
    await Promise.all([loadStats(), loadInvoices(), loadGame()]);
  };

  const refreshing = statsLoading || invoicesLoading;
  const hasStats = !!data;

  const tf = (data && data[timeframe]) || {};
  const all = (data && data.all) || {};
  const lifetime = (data && data.lifetime) || null;
  const {
    sold_items: time_sold = 0,
    total_revenue = 0,
    total_sales = 0,             // alias of total_revenue
    total_spend = 0,             // purchases in this window (incl. unsold)
    inventory_cost: tf_inv_cost = 0,
    total_platform_fees = 0,
    total_shipping_cost = 0,
    total_refunds = 0,
    total_manual_expenses = 0,
    total_profit = null,
    bar_data = [],
    sales_by_platform = [],
  } = tf;

  // Prefer backend-computed total_profit; fall back to local recompute.
  const profit = (total_profit !== null && total_profit !== undefined)
    ? total_profit
    : (total_sales - total_spend - total_platform_fees - total_shipping_cost - total_refunds - total_manual_expenses);

  const revenue = total_revenue || total_sales || 0;
  const roi = total_spend > 0 ? (profit / total_spend) * 100 : 0;

  // Inventory — always current state.
  const inventoryCount = all.inventory_count ?? 0;
  const inventoryValue = all.total_value ?? 0;
  const inventoryCost = tf_inv_cost || all.inventory_cost || 0;
  const allSold = all.sold_items ?? 0;
  const allBought = all.items_purchased ?? all.total_item_count ?? 0;
  const allSpend = all.total_spend ?? 0;
  const avgCost = allBought > 0 ? allSpend / allBought : 0;
  const sellThroughBase = allBought + inventoryCount;
  const sellThrough = sellThroughBase > 0 ? (allSold / sellThroughBase) * 100 : 0;

  const tfLabel = TIMEFRAMES.find((t) => t.id === timeframe)?.label || timeframe;

  const pb = (game?.personal_bests && game.personal_bests[timeframe]) || {};
  const streak = game?.streak;

  return (
    <Box>
      {/* Page header — matches OC Inventory.js pattern */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: "1.85rem", md: "2.5rem" } }}>
            Dashboard
          </Typography>
          <Stack direction="row" alignItems="center" gap={1.25} sx={{ flexWrap: "wrap" }}>
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              Last {tfLabel}
            </Typography>
            {streak && streak.current > 0 && (
              <Chip
                size="small"
                icon={<Flame size={14} style={{ color: streak.alive ? "#f97316" : "#9ca3af" }} />}
                label={
                  streak.current === streak.best && streak.best > 1
                    ? `${streak.current}-day buying streak — all-time best!`
                    : `${streak.current}-day buying streak${streak.best > streak.current ? ` (best: ${streak.best})` : ""}`
                }
                sx={{
                  fontWeight: 600,
                  color: streak.alive ? "warning.dark" : "text.secondary",
                  bgcolor: streak.alive ? "rgba(249,115,22,0.10)" : "transparent",
                  border: "1px solid",
                  borderColor: streak.alive ? "rgba(249,115,22,0.35)" : "divider",
                  "& .MuiChip-icon": { ml: 0.75 },
                }}
              />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={timeframe}
            onChange={(_, v) => { if (v) setTimeframe(v); }}
          >
            {TIMEFRAMES.map((t) => (
              <ToggleButton key={t.id} value={t.id} sx={{ textTransform: "none", px: 1.5 }}>
                {t.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={refreshing ? <CircularProgress size={15} color="inherit" /> : <RefreshIcon />}
            onClick={refresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Top stat row */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          mb: 3,
        }}
      >
        {!hasStats ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2.25 }}>
                <Skeleton variant="text" width={80} height={14} />
                <Skeleton variant="text" width={120} height={32} sx={{ mt: 0.5 }} />
                <Skeleton variant="text" width={140} height={14} />
              </Paper>
            ))}
          </>
        ) : (
          <>
            <StatCard
              label="Profit"
              numericValue={profit}
              formatter={currency}
              sublabel={`ROI ${percent(roi)}`}
              valueColor={profit >= 0 ? "success.main" : "error.main"}
              icon={<TrendingUpIcon fontSize="small" />}
              personalBest={pb.profit}
            />
            <StatCard
              label="Revenue"
              numericValue={revenue}
              formatter={currency}
              sublabel={`${time_sold} order${time_sold === 1 ? "" : "s"} sold`}
              valueColor="primary.main"
              icon={<AttachMoneyIcon fontSize="small" />}
              personalBest={pb.revenue}
            />
            <StatCard
              label="Spend"
              numericValue={total_spend}
              formatter={currency}
              sublabel={`Fees ${currency(total_platform_fees)} • Ship ${currency(total_shipping_cost)}`}
              icon={<ShoppingCartIcon fontSize="small" />}
              personalBest={pb.spend}
            />
            <StatCard
              label="Inventory"
              numericValue={inventoryCount}
              formatter={(n) => compact(Math.round(n))}
              sublabel={`Cost ${currency(inventoryCost)} • Worth ${currency(inventoryValue)}`}
              icon={<InventoryIcon fontSize="small" />}
            />
          </>
        )}
      </Box>

      {/* Lifetime cash position: revenue − every dollar spent on inventory −
          every operating expense. Unlike the timeframe "Profit" tile above
          (which only counts COGS for items that sold), this one subtracts the
          full purchase cost of EVERY pallet ever bought, so it goes
          negative until enough inventory sells through to recoup it. */}
      {lifetime && <LifetimeCashPosition lifetime={lifetime} />}

      {/* Profit chart + platform pie */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          mb: 2,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ mb: 0.25 }}>Gross profit per day</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Sale price − fees − shipping − refunds • {tfLabel}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={currency(profit)}
              color={profit >= 0 ? "success" : "error"}
              variant="outlined"
            />
          </Stack>
          <Box sx={{ height: 260 }}>
            {!hasStats ? (
              <Skeleton variant="rectangular" height="100%" />
            ) : bar_data.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", color: "text.secondary", fontSize: 14 }}>
                No sales in this window.
              </Stack>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={bar_data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34d399" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#34d399" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(0,0,0,0.06)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" stroke="#6b7281" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#6b7281" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${compact(v)}`} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="profit" stroke="#34d399" fill="url(#profitGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h3" sx={{ mb: 2 }}>Platforms</Typography>
          <Box sx={{ height: 200 }}>
            {!hasStats ? (
              <Skeleton variant="rectangular" height="100%" />
            ) : sales_by_platform.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", color: "text.secondary", fontSize: 14 }}>
                No platform data.
              </Stack>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sales_by_platform}
                    dataKey="count"
                    nameKey="platform"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={3}
                    stroke="rgba(0,0,0,0)"
                  >
                    {sales_by_platform.map((_, idx) => (
                      <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>
          <Stack component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: 1, gap: 0.5 }}>
            {sales_by_platform.map((p, i) => (
              <Stack
                key={p.platform || i}
                component="li"
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ fontSize: 12 }}
              >
                <Stack direction="row" alignItems="center" gap={1} sx={{ color: "text.secondary" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span>{p.platform || "Unknown"}</span>
                </Stack>
                <Typography component="span" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
                  {p.count}{" "}
                  <Typography component="span" sx={{ color: "text.disabled", fontSize: 12 }}>
                    • {currency(p.revenue || 0)}
                  </Typography>
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      </Box>

      {/* Sales bar + sell-through */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          mb: 2,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Typography variant="h3">Revenue</Typography>
            <Chip size="small" label={currency(revenue)} color="primary" variant="outlined" />
          </Stack>
          <Box sx={{ height: 200 }}>
            {!hasStats ? (
              <Skeleton variant="rectangular" height="100%" />
            ) : bar_data.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", color: "text.secondary", fontSize: 14 }}>
                No sales in this window.
              </Stack>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bar_data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(0,0,0,0.06)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" stroke="#6b7281" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#6b7281" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${compact(v)}`} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="revenue" fill="#818cf8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h3">Sell-through</Typography>
            <PercentIcon fontSize="small" sx={{ color: "text.disabled" }} />
          </Stack>
          <Typography sx={{ fontSize: 30, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
            {percent(sellThrough)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
            {compact(allSold)} sold of {compact(sellThroughBase)} ever acquired
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, sellThrough)}
            sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
          />
          <Divider sx={{ my: 1.5 }} />
          <Stack gap={0.75} sx={{ fontSize: 12, color: "text.secondary" }}>
            <Stack direction="row" justifyContent="space-between">
              <span>Avg cost / item</span>
              <Typography component="span" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
                {currency(avgCost)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <span>In stock</span>
              <Typography component="span" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
                {compact(inventoryCount)}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      {/* Invoice table */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h3">Invoice analytics</Typography>
          <Chip
            size="small"
            icon={<DescriptionIcon sx={{ fontSize: 14 }} />}
            label={invoiceData ? invoiceData.length : "…"}
            variant="outlined"
          />
        </Stack>
        {invoiceData === null ? (
          <Stack gap={1}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={20} />
            ))}
          </Stack>
        ) : (
          <InvoiceStatsTable data={invoiceData} onChanged={loadInvoices} />
        )}
      </Paper>

      <Confetti trigger={confettiKey || null} />
    </Box>
  );
}

function InvoiceStatsTable({ data, onChanged }) {
  const [sortConfig, setSortConfig] = useState({ key: "purchase_date", direction: "desc" });
  // Per-row action menu state. ``menuFor`` carries the invoice_id of
  // the row whose kebab was clicked; ``anchorEl`` is the DOM node the
  // menu pops out of. ``busy`` (the invoice id mid-mutation) disables
  // the menu so a slow network can't fire the same action twice.
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [busy, setBusy] = useState(null);
  const { success: toastSuccess, error: toastError } = useToast();

  const closeMenu = () => { setAnchorEl(null); setMenuFor(null); };

  const handleMark = async (action) => {
    if (!menuFor) return closeMenu();
    const invoice_id = menuFor;
    closeMenu();
    if (action === "refunded") {
      if (!window.confirm(
        `Mark invoice ${invoice_id} as Refunded?\n\n` +
        "Items stay in the DB with location='Refunded' for the audit trail, " +
        "and the purchases cost is zeroed (operator got the money back). " +
        "Invoice still appears in analytics but no longer drags down the dashboard."
      )) return;
    } else if (action === "abandoned") {
      if (!window.confirm(
        `Mark invoice ${invoice_id} as Abandoned?\n\n` +
        "Items stay in the DB with location='Abandoned' (full cost shows " +
        "as a write-off in PnL). Block-listed so the importer leaves it alone."
      )) return;
    }
    setBusy(invoice_id);
    try {
      const path = action === "refunded"
        ? `/api/invoice/${encodeURIComponent(invoice_id)}/delete`
        : `/api/invoice/${encodeURIComponent(invoice_id)}/mark_abandoned`;
      const r = await apiPost(path);
      if (r?.success) {
        toastSuccess(`Invoice ${invoice_id} marked ${action}`);
        if (typeof onChanged === "function") onChanged();
      } else {
        toastError(`Mark ${action} failed`, r?.error || "Unknown error");
      }
    } catch (e) {
      toastError(`Mark ${action} failed`, e?.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const sorted = useMemo(() => {
    const arr = Array.isArray(data) ? [...data] : [];
    if (!sortConfig.key) return arr;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sortConfig.key];
      const bv = b[sortConfig.key];
      const aMissing = av === null || av === undefined || av === "";
      const bMissing = bv === null || bv === undefined || bv === "";
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      const aStr = String(av).toLowerCase();
      const bStr = String(bv).toLowerCase();
      if (aStr < bStr) return -1 * dir;
      if (aStr > bStr) return 1 * dir;
      return 0;
    });
    return arr;
  }, [data, sortConfig]);

  const requestSort = (key) =>
    setSortConfig((cur) =>
      cur.key === key
        ? { key, direction: cur.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );

  const SortableHeader = ({ label, k, align = "left" }) => {
    const active = sortConfig.key === k;
    return (
      <TableCell
        align={align}
        onClick={() => requestSort(k)}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 600,
          color: "text.secondary",
          "&:hover": { color: "text.primary" },
        }}
      >
        <Stack
          direction={align === "right" ? "row-reverse" : "row"}
          alignItems="center"
          gap={0.5}
          sx={{ display: "inline-flex" }}
        >
          {label}
          {active ? (
            sortConfig.direction === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
          ) : null}
        </Stack>
      </TableCell>
    );
  };

  function getInvoiceUrl(invoice) {
    const platform = String(invoice.purchased_at || "").toLowerCase().trim();
    const id = encodeURIComponent(invoice.invoice_id || "");
    if (platform === "wiz" || platform === "wizbid") return `https://wiz.bid/invoice/auction?id=${id}`;
    if (platform === "rasmus") return `https://rasmus.com/invoices/${id}`;
    if (platform === "bidrl") return `https://www.bidrl.com/myaccount/invoice/invid/${id}`;
    return null;
  }

  const profitColor = (v) => (v > 0 ? "success.main" : v < 0 ? "error.main" : "text.secondary");

  return (
    <TableContainer>
      <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
        <TableHead>
          <TableRow>
            <SortableHeader label="Date" k="purchase_date" />
            <TableCell sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, color: "text.secondary" }}>
              Invoice
            </TableCell>
            <SortableHeader label="Items" k="item_count" align="right" />
            <SortableHeader label="STR" k="sell_through_rate" align="right" />
            <SortableHeader label="Paid" k="total_paid" align="right" />
            <SortableHeader label="Unsold $" k="unsold_investment" align="right" />
            <SortableHeader label="Revenue" k="total_revenue" align="right" />
            <SortableHeader label="Profit" k="total_profit" align="right" />
            <SortableHeader label="ROI" k="roi_percentage" align="right" />
            <SortableHeader label="Net" k="net_position" align="right" />
            <TableCell sx={{ width: 36 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ py: 6, color: "text.secondary" }}>
                No invoices.
              </TableCell>
            </TableRow>
          )}
          {sorted.map((invoice) => {
            const url = getInvoiceUrl(invoice);
            return (
              <TableRow key={invoice.invoice_id} hover>
                <TableCell sx={{ color: "text.secondary" }}>{formatShortDate(invoice.purchase_date)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.78rem" }}>
                  {url ? (
                    <Box
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                    >
                      {invoice.invoice_id}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ color: "text.secondary" }}>{invoice.invoice_id}</Box>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                  {invoice.sold_count} / {invoice.item_count}
                </TableCell>
                <TableCell align="right" sx={{ color: "primary.main", fontVariantNumeric: "tabular-nums" }}>
                  {percent(invoice.sell_through_rate)}
                </TableCell>
                <TableCell align="right" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                  {currency(invoice.total_paid)}
                </TableCell>
                <TableCell align="right" sx={{ color: "warning.main", fontVariantNumeric: "tabular-nums" }}>
                  {currency(invoice.unsold_investment)}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {currency(invoice.total_revenue)}
                </TableCell>
                <TableCell align="right" sx={{ color: profitColor(invoice.total_profit), fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                  {currency(invoice.total_profit)}
                </TableCell>
                <TableCell align="right" sx={{ color: profitColor(invoice.roi_percentage), fontVariantNumeric: "tabular-nums" }}>
                  {percent(invoice.roi_percentage)}
                </TableCell>
                <TableCell align="right" sx={{ color: profitColor(invoice.net_position), fontVariantNumeric: "tabular-nums" }}>
                  {currency(invoice.net_position)}
                </TableCell>
                <TableCell align="right" sx={{ p: 0.5 }}>
                  <IconButton
                    size="small"
                    aria-label="invoice actions"
                    disabled={busy === invoice.invoice_id}
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget);
                      setMenuFor(invoice.invoice_id);
                    }}
                  >
                    {busy === invoice.invoice_id
                      ? <CircularProgress size={14} />
                      : <MoreVertIcon fontSize="small" />}
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
      >
        <MenuItem onClick={() => handleMark("refunded")}>
          Mark as Refunded (zero cost, keep rows)
        </MenuItem>
        <MenuItem onClick={() => handleMark("abandoned")}>
          Mark as Abandoned (full-cost loss)
        </MenuItem>
      </Menu>
    </TableContainer>
  );
}

// Lifetime cash position — the "did I make money overall?" view that subtracts
// every dollar ever spent on inventory (sold or not) plus every operating
// expense. Stays negative until the remaining inventory sells through.
// Pairs each ingoing/outgoing line with a sign so the reader can audit the
// math at a glance: + green for cash in, − red for cash out.
function LifetimeCashPosition({ lifetime }) {
  const {
    revenue, cogs, spend,
    platform_fees, shipping_cost, refunds, manual_expenses,
    writeoffs_cost, writeoffs_count, trash_cost, personal_cost,
    recoverable_cost, recoverable_value, recoverable_count,
    cash_profit, pl_profit,
  } = lifetime;

  const Row = ({ label, value, sign = "-", muted, sublabel }) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      sx={{ fontSize: 13, py: 0.4 }}
    >
      <Box>
        <Typography component="span" sx={{ fontSize: 13, color: muted ? "text.disabled" : "text.primary" }}>
          {label}
        </Typography>
        {sublabel && (
          <Typography component="span" sx={{ fontSize: 11, color: "text.disabled", ml: 0.75 }}>
            {sublabel}
          </Typography>
        )}
      </Box>
      <Typography
        component="span"
        sx={{
          fontVariantNumeric: "tabular-nums",
          fontSize: 13,
          color: sign === "+" ? "success.main" : "error.main",
          fontWeight: 500,
        }}
      >
        {sign}{currency(Math.abs(value || 0))}
      </Typography>
    </Stack>
  );

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 3 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 2, md: 3 }}>
        {/* Headline: cash position */}
        <Box sx={{ flex: { md: "0 0 280px" } }}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", fontWeight: 600, mb: 0.5 }}>
            Lifetime cash position
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 30, md: 38 },
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.1,
              color: cash_profit >= 0 ? "success.main" : "error.main",
            }}
          >
            {currency(cash_profit)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.75, lineHeight: 1.45 }}>
            Revenue minus everything we&apos;ve ever spent — inventory, fees, shipping, refunds, and operating costs.
            Negative until the remaining inventory sells through.
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack gap={0.5} sx={{ fontSize: 12, color: "text.secondary" }}>
            <Stack direction="row" justifyContent="space-between">
              <span>P&L profit (accounting)</span>
              <Typography component="span" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 12, color: pl_profit >= 0 ? "success.main" : "error.main", fontWeight: 600 }}>
                {currency(pl_profit)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <span>Recoverable inventory (asset)</span>
              <Typography component="span" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 12 }}>
                {currency(recoverable_cost)} cost
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <span style={{ color: "transparent" }}>·</span>
              <Typography component="span" sx={{ fontVariantNumeric: "tabular-nums", fontSize: 12, color: "text.disabled" }}>
                ≈ {currency(recoverable_value)} retail · {compact(recoverable_count)} items
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Breakdown */}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", fontWeight: 600, mb: 1 }}>
            Breakdown
          </Typography>
          <Row label="Revenue (gross sales)" value={revenue} sign="+" />
          <Divider sx={{ my: 0.5 }} />
          <Row label="Inventory purchases (all)" value={spend} sign="-" sublabel={`incl. COGS ${currency(cogs)}`} />
          <Row label="Platform fees" value={platform_fees} sign="-" />
          <Row label="Shipping labels" value={shipping_cost} sign="-" />
          <Row label="Refunds + disputes" value={refunds} sign="-" />
          <Row label="Operating expenses" value={manual_expenses} sign="-" sublabel="vehicle, storage, eBay subs, etc." />
          {writeoffs_cost > 0 && (
            <Row
              label="Write-offs (trash + personal)"
              value={writeoffs_cost}
              sign="-"
              muted
              sublabel={`${writeoffs_count} items — ${currency(trash_cost)} trash · ${currency(personal_cost)} personal — already counted in purchases above`}
            />
          )}
          <Divider sx={{ my: 0.75 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Net cash position</Typography>
            <Typography
              sx={{
                fontVariantNumeric: "tabular-nums",
                fontWeight: 700,
                color: cash_profit >= 0 ? "success.main" : "error.main",
              }}
            >
              {currency(cash_profit)}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}
